import React, { useEffect } from 'react'
import { useRouter } from '@shared/hooks/use-router'
import { FormProvider, useForm } from 'react-hook-form'
import { useRequirementQuery } from './use-requirement.query.hook'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { RequirementHeader } from './requirement-header.component'
import { useStatusesQuery } from '@shared/hooks/use-statuses-query.hook'
import { RequirementDetails } from './requirement-details.component'
import { Divider } from '@mui/material'
import { RequirementActions } from './requirement-actions.component'
import {
  AuditRequirementResponse,
  RequirementFormConfig,
  RequirementPageType,
  Roles,
} from '@shared/types'
import { RequirementForm } from './requirement-form.component'
import { useACL } from '@shared/hooks/use-acl.hook'
import { format } from 'date-fns'
import Joyride from 'react-joyride'
import { onboardingConfig } from '@shared/onboarding/config'
import {
  requirementSteps,
  requirementStepsBasic,
} from '@shared/onboarding/steps'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'
import { ACL } from '@shared/components/acl.component'

type RouteQuery = { auditID: string; requirementID: string; companyID: string }

const parseObjectsIds = (objects: unknown[]) => {
  return (objects as { id: number }[]).map((item) => item.id)
}

const useGetConfigForm = ({
  type,
  requirement,
}: {
  type: RequirementPageType
  requirement?: AuditRequirementResponse
}): RequirementFormConfig => {
  const {
    query: { auditID, requirementID, companyID },
  } = useRouter<RouteQuery>()
  const { is } = useACL()
  const commonPreviewFields = {
    allowDraft: false,
    allowTemplates: false,
    prevUrl: `/company-profile/${companyID}`,
    invalidateQueriesUrl: `CompaniesEvaluationProfiles/${companyID}`,
    requirementID: requirement?.id,
    requirementBaseID: requirement?.requirementBaseId,
  }

  if (type === 'published') {
    const allowedToEditRoles = [
      Roles.ClientEditor,
      Roles.ClientAdministrator,
      Roles.Admin,
    ]

    return {
      fields: {
        clientStatusID: {
          show: is([Roles.ClientEditor, Roles.ClientAdministrator]),
          editable: is([Roles.ClientEditor, Roles.ClientAdministrator]),
          defaultValue: requirement?.clientStatus?.statusBaseId,
          saveAsKey: 'status',
        },
        connectedDocuments: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedDocuments ?? []),
        },
        connectedDecisions: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedDecisions ?? []),
        },
        connectedAspects: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedAspects ?? []),
        },
        clientComment: {
          show: true,
          editable: is([Roles.ClientEditor, Roles.ClientAdministrator]),
          defaultValue: requirement?.clientStatus?.comment,
          saveAsKey: 'statusComments',
        },
        auditorAssessment: {
          show: true,
          editable: is([Roles.Admin]),
          defaultValue: requirement?.auditorAssessment,
        },
        photos: {
          show: is([
            Roles.Client,
            Roles.ClientEditor,
            Roles.ClientAdministrator,
            Roles.Auditor,
            Roles.Admin,
          ]),
          editable: is([Roles.Admin]),
          defaultValue: requirement?.images ?? [],
        },
        fixDate: {
          show: true,
          editable: is([Roles.Admin]),
          defaultValue: requirement?.fixDate,
        },
        evaluationDate: {
          show: !is([Roles.ClientBasic]),
          editable: is([Roles.Admin]),
          defaultValue: requirement?.evaluationDate
            ? format(new Date(requirement.evaluationDate), 'dd-MM-yyyy')
            : '',
        },
        auditorStatusID: {
          show: is([Roles.Admin]),
          editable: is([Roles.Admin]),
          defaultValue: requirement?.auditorStatus?.statusBaseId,
          saveAsKey: 'status',
        },
        summary: {
          show: is([Roles.Admin]),
          editable: is([Roles.Admin]),
          defaultValue: requirement?.summary,
        },
        connectedAuditRequirements: {
          show: true,
          editable: is([Roles.Admin]),
          defaultValue: is([Roles.Admin])
            ? parseObjectsIds(requirement?.connectedAuditRequirements ?? [])
            : (requirement?.connectedAuditRequirements ?? []),
        },
      },
      allowSave: is([
        Roles.Admin,
        Roles.ClientAdministrator,
        Roles.ClientEditor,
      ]),
      endpointType: is([Roles.Admin]) ? 'correction' : 'client',
      ...commonPreviewFields,
    }
  }

  if (type === 'esqula') {
    const allowedToEditRoles = [
      Roles.ClientEditor,
      Roles.ClientAdministrator,
      Roles.Admin,
    ]

    return {
      fields: {
        connectedDocuments: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedDocuments ?? []),
        },
        connectedDecisions: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedDecisions ?? []),
        },
        connectedAspects: {
          show: true,
          editable: is(allowedToEditRoles),
          defaultValue: parseObjectsIds(requirement?.connectedAspects ?? []),
        },
        fixDate: {
          show: true,
          editable: false,
          defaultValue: requirement?.fixDate,
        },
      },
      allowSave: is(allowedToEditRoles),
      endpointType: is([Roles.Admin]) ? 'esqulaCorrection' : 'esqulaClient',
      ...commonPreviewFields,
    }
  }

  return {
    fields: {
      auditorStatusID: {
        show: true,
        editable: true,
        defaultValue: requirement?.auditorStatus?.statusBaseId,
        saveAsKey: 'status',
      },
      connectedDocuments: {
        show: true,
        editable: true,
        defaultValue: parseObjectsIds(requirement?.connectedDocuments ?? []),
      },
      connectedDecisions: {
        show: true,
        editable: true,
        defaultValue: parseObjectsIds(requirement?.connectedDecisions ?? []),
      },
      connectedAspects: {
        show: true,
        editable: true,
        defaultValue: parseObjectsIds(requirement?.connectedAspects ?? []),
      },
      clientComment: {
        show: true,
        editable: false,
        defaultValue: requirement?.clientStatus?.comment,
      },
      auditorAssessment: {
        show: true,
        editable: true,
        defaultValue: requirement?.auditorAssessment,
      },
      photos: {
        show: true,
        editable: true,
        defaultValue: requirement?.images ?? [],
      },
      summary: {
        show: true,
        editable: true,
        defaultValue: requirement?.summary,
      },
      connectedAuditRequirements: {
        show: true,
        editable: true,
        defaultValue: parseObjectsIds(
          requirement?.connectedAuditRequirements ?? []
        ),
      },
      fixDate: {
        show: true,
        editable: true,
        defaultValue: requirement?.fixDate,
      },
    },
    allowSave: true,
    allowDraft: true,
    allowTemplates: true,
    prevUrl: `/company-profile/${companyID}/${auditID}`,
    invalidateQueriesUrl: `companies/${companyID}/audits/${auditID}`,
    endpointType: 'auditor',
    requirementID: requirement?.id,
    requirementBaseID: requirement?.requirementBaseId,
    auditID: auditID,
  }
}

export function RequirementAssessmentPage({
  type,
}: {
  type: RequirementPageType
}) {
  const {
    query: { auditID, requirementID, companyID },
  } = useRouter<RouteQuery>()
  const { isActive, setActive } = useOnboardingStore()
  const { is } = useACL()
  const { statuses, areStatusesLoading } = useStatusesQuery()
  const formMethods = useForm()
  const { requirement, isLoading } = useRequirementQuery(
    type,
    companyID,
    auditID,
    requirementID
  )
  const onboardingSteps = is([Roles.ClientBasic, Roles.Client])
    ? requirementStepsBasic
    : requirementSteps

  const formConfig = useGetConfigForm({
    type,
    requirement,
  })

  useEffect(() => {
    if (requirement) {
      const values = Object.entries(formConfig.fields).reduce(
        (acc, [key, config]) => {
          if (config.show) {
            acc[key] = config.defaultValue
          }
          return acc
        },
        {} as Record<string, any>
      )

      formMethods.reset(values)
    }
  }, [requirement])

  useEffect(() => {
    document.querySelector('main')?.scrollTo(0, 0)
  }, [])

  if (isLoading || areStatusesLoading) {
    return <BackdropProgress />
  }

  if (!requirement || !requirement.requirementBaseId) {
    return null
  }

  return (
    <>
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          {...onboardingConfig}
          steps={onboardingSteps}
          continuous
          run={isActive(OnboardingType.REQUIREMENT_ASSESSMENT_PAGE)}
          callback={(data) => {
            if (data.status === 'finished' || data.status === 'skipped') {
              setActive(OnboardingType.REQUIREMENT_ASSESSMENT_PAGE, false)
            }
          }}
        />
      </ACL>
      <FormProvider {...formMethods}>
        <RequirementHeader
          requirement={requirement}
          statuses={statuses!}
          isAudit={type !== 'esqula'}
        />
        <RequirementDetails requirement={requirement} type={type} />
        <Divider />
        <RequirementForm formConfig={formConfig} companyID={companyID} />
        <RequirementActions
          formConfig={formConfig}
          companyID={companyID}
          auditID={auditID}
        />
      </FormProvider>
    </>
  )
}
